<template>
  <div :class="ns.b('panel')">
    <p :class="ns.be('panel', 'header')">
      <z-checkbox v-model="allChecked" :indeterminate="isIndeterminate" :validate-event="false"
        @change="handleAllCheckedChange">
        <span :class="ns.be('panel', 'header-title')">{{ title }}</span>
        <span :class="ns.be('panel', 'header-count')">
          {{ checkedSummary }}
        </span>
      </z-checkbox>
    </p>

    <div :class="[ns.be('panel', 'body'), ns.is('with-footer', hasFooter)]">
      <z-input v-if="filterable" v-model="query" :class="ns.be('panel', 'filter')" size="default"
        :placeholder="placeholder" :prefix-icon="Search" clearable :validate-event="false" />
      <z-checkbox-group v-show="!hasNoMatch && !isEmpty(data)" v-model="checked" :validate-event="false"
        :class="[ns.is('filterable', filterable), ns.be('panel', 'list')]">
        <z-checkbox v-for="item in filteredData" :key="item[propsAlias.key]" :class="ns.be('panel', 'item')"
          :value="item[propsAlias.key]" :disabled="item[propsAlias.disabled]" :validate-event="false">
          <option-content :option="optionRender?.(item)" />
        </z-checkbox>
      </z-checkbox-group>
      <div v-show="hasNoMatch || isEmpty(data)" :class="ns.be('panel', 'empty')">
        <slot name="empty">
          {{ hasNoMatch ? t('z.transfer.noMatch') : t('z.transfer.noData') }}
        </slot>
      </div>
    </div>
    <p v-if="hasFooter" :class="ns.be('panel', 'footer')">
      <slot />
    </p>
  </div>
</template>

<script lang="ts" setup generic="T extends TransferDataItem = TransferDataItem">
import { computed, reactive, toRefs, useSlots } from 'vue'
import { isEmpty, mutable } from '@zoryn/utils'
import { useLocale, useNamespace } from '@zoryn/hooks'
import { ZCheckbox, ZCheckboxGroup } from '@zoryn/components/checkbox'
import { ZInput } from '@zoryn/components/input'
import { Search } from '@zoryn/icons'
import { transferPanelEmits } from './transfer-panel'
import { useCheck, usePropsAlias } from './composables'

import type { VNode } from 'vue'
import type { TransferDataItem } from './transfer'
import type { TransferPanelProps, TransferPanelState } from './transfer-panel'

defineOptions({
  name: 'ZTransferPanel',
})

const props = withDefaults(defineProps<TransferPanelProps<T>>(), {
  data: () => [],
  format: () => ({}),
  defaultChecked: () => [],
  props: () =>
    mutable({
      label: 'label',
      key: 'key',
      disabled: 'disabled',
    }),
})
const emit = defineEmits(transferPanelEmits)
const slots = useSlots()

const OptionContent = ({ option }: { option?: VNode | VNode[] }) => option

const { t } = useLocale()
const ns = useNamespace('transfer')

const panelState = reactive<TransferPanelState>({
  checked: [],
  allChecked: false,
  query: '',
  checkChangeByUser: true,
})

const propsAlias = usePropsAlias(props)

const {
  filteredData,
  checkedSummary,
  isIndeterminate,
  handleAllCheckedChange,
} = useCheck(props, panelState, emit)

const hasNoMatch = computed(
  () => !isEmpty(panelState.query) && isEmpty(filteredData.value)
)

const hasFooter = computed(() => !isEmpty(slots.default!()[0].children))

const { checked, allChecked, query } = toRefs(panelState)

defineExpose({
  /** @description filter keyword */
  query,
})
</script>
